import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import type { Company, Supervisor, Employee } from "@/types";
import {
  CompanyDetailHeader,
  SupervisorsSection,
  EmployeesSection,
  CreditSection,
  BillingTermsSection,
  CompanyInvoicesSection,
  PaymentRequestsSection,
} from "@/components/admin/companies";

export const Route = createFileRoute(
  "/dashboard/admin/companies/$companyId"
)({
  component: CompanyDetail,
});

function CompanyDetail() {
  const { companyId } = Route.useParams();
  const queryClient = useQueryClient();
  const [removingSupervisorId, setRemovingSupervisorId] = useState<string | null>(null);

  const { data: company, isLoading } = useQuery({
    queryKey: ["admin", "company", companyId],
    queryFn: async () => {
      const res = await fetch(`/api/companies/${companyId}`);
      await throwIfNotOk(res, "Greška pri učitavanju firme");
      return res.json() as Promise<Company>;
    },
  });

  const { data: supervisors = [] } = useQuery({
    queryKey: ["admin", "companies", companyId, "supervisors"],
    queryFn: async () => {
      const res = await fetch(`/api/companies/${companyId}/supervisors`);
      await throwIfNotOk(res, "Greška pri učitavanju supervizora");
      return res.json() as Promise<Supervisor[]>;
    },
    enabled: !!company,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["admin", "companies", companyId, "employees"],
    queryFn: async () => {
      const res = await fetch(`/api/companies/${companyId}/employees`);
      await throwIfNotOk(res, "Greška pri učitavanju zaposlenih");
      return res.json() as Promise<Employee[]>;
    },
    enabled: !!company,
  });

  const removeSupervisorMutation = useMutation({
    mutationFn: async (supervisorId: string) => {
      const res = await fetch(
        `/api/companies/${companyId}/supervisors/${supervisorId}`,
        { method: "DELETE" }
      );
      await throwIfNotOk(res, "Greška pri uklanjanju supervizora");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "companies", companyId, "supervisors"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "company", companyId] });
      toast.success("Supervizor uklonjen");
      setRemovingSupervisorId(null);
    },
  });

  if (isLoading || !company) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="h-8 w-32 animate-pulse rounded bg-muted" />
        <div className="h-10 w-64 animate-pulse rounded bg-muted" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl border bg-card" />
          ))}
        </div>
        <div className="h-64 animate-pulse rounded-xl border bg-card" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <CompanyDetailHeader company={company} />

      <BillingTermsSection company={company} />

      <PaymentRequestsSection companyId={companyId} />

      {/* Firma koja je prešla na fakturu i dalje vidi preostali kredit i historiju. */}
      {(company.billingModel === "credit" || (company.creditBalance ?? 0) > 0) && (
        <CreditSection companyId={companyId} balance={company.creditBalance ?? 0} />
      )}

      {/* Firma prebačena na kredit i dalje vidi i dovršava fakture iz ranijeg perioda. */}
      <CompanyInvoicesSection companyId={companyId} hideWhenEmpty={company.billingModel !== "invoice"} />

      <SupervisorsSection
        companyId={companyId}
        supervisors={supervisors}
        onRemove={setRemovingSupervisorId}
        isRemoving={removeSupervisorMutation.isPending}
      />

      <EmployeesSection companyId={companyId} employees={employees} />

      <ConfirmDialog
        open={removingSupervisorId !== null}
        title="Ukloni supervizora"
        description="Da li ste sigurni da želite ukloniti ovog supervizora?"
        confirmLabel="Ukloni"
        tone="destructive"
        isPending={removeSupervisorMutation.isPending}
        onClose={() => setRemovingSupervisorId(null)}
        onConfirm={() => removingSupervisorId && removeSupervisorMutation.mutate(removingSupervisorId)}
      />
    </div>
  );
}
